import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/features/auth/AuthContext'
import { isNavGroup, type NavEntry } from '@/features/auth/types'
import { useSidebarState } from './useSidebarState'
import { MIGRATED_ROUTES } from './migratedRoutes'

/**
 * Renders exactly what GET /v2/auth/session's `nav` field returns — the same
 * permission-filtered tree internal/core/config/navigation_registry.py
 * computes for the legacy sidebar. No permission logic is duplicated here;
 * an item simply isn't in the list if the user can't see it.
 */
export default function Sidebar() {
  const { nav } = useAuth()
  const { expanded, toggle } = useSidebarState()
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})

  // A client role with no permission-gated nav entries at all (the common
  // case - clients only ever see their own portfolio/device pages, reached
  // via direct links rather than a sidebar) would otherwise still render
  // an empty 64-220px nav rail plus its expand/collapse toggle, wasting
  // that width for no navigable content. Rendering nothing here instead
  // lets AppLayout's flex row give <main> the full width automatically -
  // no change needed there.
  if (nav.length === 0) return null

  function toggleGroup(key: string) {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function renderIcon(svg: string) {
    return (
      <span
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    )
  }

  /** Internal route if this nav slot has been migrated to React, else null. */
  function migratedPath(key: string): string | null {
    return MIGRATED_ROUTES[key] ?? null
  }

  function renderEntry(entry: NavEntry) {
    if (isNavGroup(entry)) {
      const isOpen = openGroups[entry.key] ?? false
      return (
        <li key={entry.key}>
          <button
            type="button"
            onClick={() => toggleGroup(entry.key)}
            title={entry.label}
            className="flex w-full items-center gap-2 px-4 py-3 text-left text-brand hover:bg-brand hover:text-white"
          >
            {renderIcon(entry.icon_svg)}
            {expanded && <span className="flex-1 truncate text-sm">{entry.label}</span>}
          </button>
          {isOpen && expanded && (
            <ul className="bg-bg-tertiary">
              {entry.children.map((child) => {
                const childClassName =
                  'flex items-center gap-2 py-2 pl-10 pr-4 text-sm text-text-primary hover:bg-brand hover:text-white'
                const to = migratedPath(child.key)
                return (
                  <li key={child.key}>
                    {to ? (
                      <NavLink to={to} className={childClassName}>
                        {renderIcon(child.icon_svg)}
                        <span className="truncate">{child.label}</span>
                      </NavLink>
                    ) : (
                      <a href={child.href} className={childClassName}>
                        {renderIcon(child.icon_svg)}
                        <span className="truncate">{child.label}</span>
                      </a>
                    )}
                  </li>
                )
              })}
            </ul>
          )}
        </li>
      )
    }

    const linkClassName = 'flex items-center gap-2 px-4 py-3 text-brand hover:bg-brand hover:text-white'
    const to = migratedPath(entry.key)
    return (
      <li key={entry.key}>
        {to ? (
          <NavLink to={to} title={entry.label} className={linkClassName}>
            {renderIcon(entry.icon_svg)}
            {expanded && <span className="truncate text-sm">{entry.label}</span>}
          </NavLink>
        ) : (
          <a href={entry.href} title={entry.label} className={linkClassName}>
            {renderIcon(entry.icon_svg)}
            {expanded && <span className="truncate text-sm">{entry.label}</span>}
          </a>
        )}
      </li>
    )
  }

  return (
    <nav
      className={`relative h-full shrink-0 overflow-y-auto border-r border-border bg-sidebar transition-[width] duration-200 ${
        expanded ? 'w-[220px]' : 'w-[64px]'
      }`}
    >
      <ul>{nav.map(renderEntry)}</ul>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
        className="absolute -right-3 top-2 flex h-7 w-7 items-center justify-center rounded-full border border-border bg-bg-tertiary text-brand shadow-sm hover:bg-brand hover:text-white"
      >
        <span className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}>›</span>
      </button>
    </nav>
  )
}
